// HexGrid orchestrator — partitions tiles by biome then delegates to
// TerrainTiles, TerrainWater, and TerrainScatter. Resource markers
// rendered directly. Re-exports HEX_SIZE, HEX_HEIGHT, axialToWorld
// for backward compatibility.
import type { SceneNode } from '@aod/protocol';
import { useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { HexSilhouettes } from './HexSilhouettes.js';
import { RenderLayer } from './RenderLayer.js';
import { TerrainScatter, WindAnimator } from './TerrainScatter.js';
import { RiverAnimator, TerrainTiles, buildTileBaseColors } from './TerrainTiles.js';
import { TerrainWater, WaterAnimator } from './TerrainWater.js';
import { Volcanoes } from './Volcanoes.js';
import { Waterfalls } from './Waterfalls.js';
import { buildBiomeMap, setTileLevels, terrainLevelsActive, tileElevationAt } from './elevation.js';
import { HEX_HEIGHT, HEX_SIZE } from './hexLayout.js';
import { wallStyle } from './sea/wallStyle.js';
import { groundSeasonTint } from './seasons.js';
import { ShorelineMesh } from './shore/ShorelineMesh.js';
import { setShoreField, shoreBoardFor } from './shore/shoreBoard.js';
import { tileDataFor } from './tileData.js';
import { deriveVolcanoes, volcanoTileCodes } from './volcano.js';

export { HEX_HEIGHT, HEX_SIZE };

// Module-level singletons mutated in place during resource-marker updates.
const _dummy = new THREE.Object3D();
const _color = new THREE.Color();

interface HexGridProps {
  readonly tiles: ReadonlyArray<SceneNode>;
  readonly resourceColors?: Readonly<Record<string, string>>;
  readonly visibleCoords?: Set<string> | null;
  readonly exploredCoords?: Set<string>;
  readonly onPointerOver?: (tile: SceneNode | null) => void;
  readonly onClick?: (tile: SceneNode) => void;
  /** "q,r" keys of settlement hexes; scatter trees are kept off them. */
  readonly settlementCoords?: ReadonlySet<string>;
  /** 1-based day of year — drives the season tint on tiles + scatter. */
  readonly seasonDay?: number;
  /** Map climate (tropical maps get jungle instead of temperate forest). */
  readonly climate?: string;
  /** Scatter density multiplier tier (1 = normal, 2 = Large Board). */
  readonly detail?: number;
}

function biomeOf(tile: SceneNode): string {
  const props = tile.props as Record<string, unknown> | undefined;
  return (props?.biome as string) ?? 'plains';
}

function groupByBiome(
  tiles: ReadonlyArray<SceneNode>,
): Map<string, Array<{ tile: SceneNode; idx: number }>> {
  const groups = new Map<string, Array<{ tile: SceneNode; idx: number }>>();
  tiles.forEach((tile, idx) => {
    const biome = biomeOf(tile);
    let g = groups.get(biome);
    if (!g) {
      g = [];
      groups.set(biome, g);
    }
    g.push({ tile, idx });
  });
  return groups;
}

export function HexGrid({
  tiles,
  resourceColors,
  visibleCoords,
  exploredCoords,
  onPointerOver,
  onClick,
  settlementCoords,
  seasonDay = 0,
  climate,
  detail = 1,
}: HexGridProps) {
  const groups = useMemo(() => groupByBiome(tiles), [tiles]);
  const biomeMap = useMemo(() => buildBiomeMap(tiles), [tiles]);
  // Day-granular: the tint (and every downstream instance colour) only
  // recomputes when the in-game day changes, not per tick.
  const seasonTint = useMemo(
    () => (seasonDay > 0 ? groundSeasonTint(seasonDay) : null),
    [seasonDay],
  );
  const baseColors = useMemo(() => buildTileBaseColors(tiles, seasonTint), [tiles, seasonTint]);
  // Terrain levels (terrain.levels module): publish coord→level so every
  // tileSurfaceY/surfaceYAt consumer re-heights. Empty when the module is
  // off (no tile carries the prop) → legacy biome-derived heights.
  const walls = useMemo(() => wallStyle(tiles), [tiles]); // landlocked preview wall (empty normally)
  useMemo(() => {
    const map = new Map<string, number>();
    for (const t of tiles) {
      const props = t.props as Record<string, unknown> | undefined;
      const q = props?.q as number | undefined;
      const r = props?.r as number | undefined;
      const level = props?.level as number | undefined;
      const tb = props?.biome;
      if (tb === 'ocean' || tb === 'coast' || tb === 'river') continue; // water keeps its own planes
      if (q !== undefined && r !== undefined && level !== undefined) {
        map.set(`${q},${r}`, level);
      }
    }
    setTileLevels(map.size > 0 ? map : null, walls.boosts);
    return map;
  }, [tiles, walls]);

  // Shore data (edges, world-space distance-to-land field, beach ramp): cached by terrain signature, so it is
  // rebuilt only when the board changes, never per publish. After the levels publish above (heights).
  const shore = shoreBoardFor(tiles);
  useMemo(() => setShoreField(shore.field), [shore]);

  // Volcano tile codes for the land shader (ash flanks, no snow). Derived after
  // the levels publish above; empty (and referentially stable) off levelled boards.
  const volcanoCodes = useMemo(() => {
    if (!terrainLevelsActive()) return undefined;
    const codes = volcanoTileCodes(deriveVolcanoes(tiles));
    for (const [k, c] of walls.codes) codes.set(k, c);
    return codes;
  }, [tiles, walls]);

  // Per-tile flag texture (tileData.ts, P7.13): rivers, volcano/wall codes
  // and levels as RGBA8, cached per terrain signature so the fresh snapshot
  // array never rebuilds it.
  const tileData = useMemo(() => tileDataFor(tiles, volcanoCodes), [tiles, volcanoCodes]);

  if (tiles.length === 0) return null;

  return (
    <group>
      {Array.from(groups.entries()).map(([biome, entries]) => {
        const isWater = biome === 'ocean' || biome === 'coast' || biome === 'river';
        if (isWater) {
          return (
            <TerrainWater
              key={biome}
              entries={entries}
              biome={biome}
              count={entries.length}
              biomeMap={biomeMap}
              baseColors={baseColors}
              visibleCoords={visibleCoords}
              exploredCoords={exploredCoords}
            />
          );
        }
        return (
          <TerrainTiles
            key={biome}
            entries={entries}
            biome={biome}
            count={entries.length}
            visibleCoords={visibleCoords}
            exploredCoords={exploredCoords}
            onPointerOver={onPointerOver}
            onClick={onClick}
            biomeMap={biomeMap}
            baseColors={baseColors}
            tileData={tileData}
          />
        );
      })}
      <ShorelineMesh board={shore} visibleCoords={visibleCoords} exploredCoords={exploredCoords} />
      <TerrainScatter
        tiles={tiles}
        clearCoords={settlementCoords}
        seasonDay={seasonDay}
        detail={detail}
        climate={climate}
      />
      <HexSilhouettes tiles={tiles} biomeMap={biomeMap} />
      <Waterfalls tiles={tiles} biomeMap={biomeMap} visibleCoords={visibleCoords} />
      <Volcanoes tiles={tiles} biomeMap={biomeMap} />
      <ResourceMarkers
        entries={tiles.map((t, i) => ({ tile: t, idx: i }))}
        resourceColors={resourceColors}
        biomeMap={biomeMap}
      />
      <WaterAnimator />
      <WindAnimator />
      <RiverAnimator />
    </group>
  );
}

// --- Resource markers (preserved from original) ---

const resourceMarkerGeo = new THREE.OctahedronGeometry(0.15, 0);

const DEFAULT_RESOURCE_COLORS: Record<string, string> = {
  iron: '#b0a0a0',
  silver: '#c8d0d8',
  gold: '#ffd700',
  crystal: '#c880ff',
  crystals: '#c880ff',
  crystal_mine: '#9955cc',
  stone: '#a09080',
  mana: '#4488ff',
  wood: '#5a8a3c',
  food: '#88bb44',
};

// Per-resource scale multiplier so rare deposits (crystal/gold/mana) read
// larger and pop visually against the more common ones (wood/stone/food).
// Tier 3 = rare/premium; tier 2 = standard; tier 1 = common.
const RESOURCE_SCALE: Record<string, number> = {
  crystal: 1.6,
  crystals: 1.6,
  crystal_mine: 1.8,
  gold: 1.5,
  mana: 1.5,
  silver: 1.2,
  iron: 1.1,
  stone: 0.9,
  wood: 0.9,
  food: 0.8,
};

function ResourceMarkers({
  entries,
  resourceColors,
  biomeMap,
}: {
  entries: Array<{ tile: SceneNode; idx: number }>;
  resourceColors?: Readonly<Record<string, string>>;
  biomeMap: Map<string, string>;
}) {
  const meshRef = useRef<THREE.InstancedMesh>(null);
  const resourceEntries = useMemo(
    () =>
      entries.filter(({ tile }) => {
        const props = tile.props as Record<string, unknown> | undefined;
        return props?.resource != null;
      }),
    [entries],
  );

  const material = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        roughness: 0.3,
        metalness: 0.4,
        emissive: new THREE.Color('#222222'),
      }),
    [],
  );

  useEffect(() => {
    const mesh = meshRef.current;
    if (!mesh || resourceEntries.length === 0) return;

    const dummy = _dummy;
    const color = _color;
    const merged = { ...DEFAULT_RESOURCE_COLORS, ...resourceColors };

    resourceEntries.forEach(({ tile }, localIdx) => {
      const [x, , z] = tile.transform.position;
      const props = tile.props as Record<string, unknown> | undefined;
      const q = (props?.q as number) ?? 0;
      const r = (props?.r as number) ?? 0;
      const elev = biomeMap ? tileElevationAt(q, r, biomeMap) : 0;
      const resource = props?.resource as string;
      const scale = RESOURCE_SCALE[resource] ?? 1;
      // Vary rotation per-instance so clusters don't look like a picket fence.
      const yaw = (localIdx * 137.5 * Math.PI) / 180;
      dummy.position.set(x, elev + 0.28 * scale, z);
      dummy.rotation.set(0, yaw, 0);
      dummy.scale.set(scale, scale, scale);
      dummy.updateMatrix();
      mesh.setMatrixAt(localIdx, dummy.matrix);

      const hex = merged[resource] ?? '#ffffff';
      color.set(hex);
      mesh.setColorAt(localIdx, color);
    });

    mesh.instanceMatrix.needsUpdate = true;
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    mesh.count = resourceEntries.length;
  }, [resourceEntries, resourceColors]);

  if (resourceEntries.length === 0) return null;

  return (
    <instancedMesh
      ref={meshRef}
      args={[resourceMarkerGeo, material, resourceEntries.length]}
      renderOrder={RenderLayer.Resources}
      frustumCulled
      castShadow
    />
  );
}

/** Pointy-top axial (q, r) → world-space (x, y, z). Must match WASM side. */
export function axialToWorld(q: number, r: number): [number, number, number] {
  const SQRT_3 = Math.sqrt(3);
  const x = HEX_SIZE * SQRT_3 * (q + r / 2);
  const z = HEX_SIZE * 1.5 * r;
  return [x, 0, z];
}
